import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, Flame, Gem } from 'lucide-react';
import CabinSidebar from '../components/cabin/CabinSidebar';
import NotificationBell from '../components/cabin/NotificationBell';
import { useCabinStats } from '../hooks/cabin/useCabinStats';
import { useLanguage } from '../contexts/LanguageContext';
import { interpolate } from '../utils/cabinI18n';

const STORAGE_KEY = 'cabin_sidebar_collapsed';

// ── Fire mode sabitleri — tek kaynak, iki bar'a pass edilir ──────────────────
const FIRE_COLOR_ICON  = '#FF4B4B';   // streak >= 7 ikon rengi
const FIRE_COLOR_TEXT  = '#FF4B4B';   // streak >= 7 yazı rengi
const NORMAL_COLOR_ICON = '#F97316';  // streak < 7 ikon rengi
const NORMAL_COLOR_TEXT = '#EA580C';  // streak < 7 yazı rengi

function getStreakColors(streak) {
  const fire = streak >= 7;
  return {
    isFireMode: fire,
    iconColor: fire ? FIRE_COLOR_ICON  : NORMAL_COLOR_ICON,
    textColor: fire ? FIRE_COLOR_TEXT  : NORMAL_COLOR_TEXT,
  };
}

/**
 * CabinGamificationBar — Tüm kabin sayfalarında görünen sticky üst bar.
 * Streak (Flame + değer) + Miles/Gem (Gem + değer) + NotificationBell.
 * Layout seviyesinde useCabinStats çağrılır; aynı React Query cache'i kullanılır.
 */
function CabinGamificationBar({ streak, miles, lang }) {
  const { t } = useLanguage();

  const streakLabel = interpolate(
    t('cabin.a11y.streak') || '{days} günlük seri',
    { days: streak }
  );
  const milesLabel = interpolate(
    t('cabin.a11y.total_miles') || '{count} mil',
    { count: miles }
  );

  // Intl.NumberFormat tek instance — locale değişince yeniden oluşturulur
  const numberFmt = useMemo(
    () => new Intl.NumberFormat(lang === 'tr' ? 'tr-TR' : 'en-US'),
    [lang]
  );
  const milesFormatted = numberFmt.format(miles);

  const { iconColor, textColor } = getStreakColors(streak);

  return (
    <div
      className="sticky top-0 z-20 flex items-center gap-2 px-4 py-2 bg-white border-b-2 border-gray-200 shadow-[0_1px_4px_rgba(0,0,0,0.06)]"
      data-testid="cabin-gamification-bar"
    >
      {/* Streak chip */}
      <div
        className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-50 border border-orange-200 select-none cursor-default"
        role="img"
        aria-label={streakLabel}
        data-testid="cabin-topbar-streak"
      >
        <Flame
          size={16}
          aria-hidden="true"
          strokeWidth={2.5}
          style={{ color: iconColor }}
        />
        <span
          className="text-xs font-black tabular-nums"
          style={{ color: textColor }}
        >
          {streak}
        </span>
      </div>

      {/* Miles/Gem chip */}
      <div
        className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-50 border border-cyan-200 select-none cursor-default"
        role="img"
        aria-label={milesLabel}
        data-testid="cabin-topbar-miles"
      >
        <Gem size={16} className="text-cyan-500" aria-hidden="true" strokeWidth={2.5} />
        <span className="text-xs font-black tabular-nums text-cyan-700">
          {milesFormatted}
        </span>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Notification Bell — tek kanonik kabin çanı */}
      <NotificationBell variant="desktop" />
    </div>
  );
}

/**
 * MobileTopBar — hamburger + logo + streak + miles + çan.
 * Dar ekran (360px) taşmasını önlemek için overflow-x-hidden.
 * Renk/isFireMode hesabı getStreakColors yardımcısından gelir.
 */
function MobileTopBar({ streak, miles, lang, onMenuOpen }) {
  const { iconColor, textColor } = getStreakColors(streak);

  // Intl.NumberFormat tek instance per locale
  const numberFmt = useMemo(
    () => new Intl.NumberFormat(lang === 'tr' ? 'tr-TR' : 'en-US'),
    [lang]
  );
  const milesFormatted = numberFmt.format(miles);

  return (
    <div className="md:hidden sticky top-0 z-20 flex items-center gap-2 px-3 py-2 bg-white border-b-2 border-gray-200 shadow-[0_1px_4px_rgba(0,0,0,0.06)] overflow-x-hidden">
      <button
        onClick={onMenuOpen}
        aria-label="Menüyü aç"
        className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] shrink-0"
        data-testid="cabin-mobile-menu-btn"
      >
        <Menu size={20} aria-hidden="true" />
      </button>
      <span className="text-sm font-black text-[var(--text-main)] shrink-0">AirAcademia</span>

      {/* Spacer */}
      <div className="flex-1 min-w-0" />

      {/* Streak chip — mobil kompakt */}
      <div
        className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-50 border border-orange-200 select-none shrink-0"
        role="img"
        aria-label={`${streak} günlük seri`}
      >
        <Flame
          size={14}
          aria-hidden="true"
          strokeWidth={2.5}
          style={{ color: iconColor }}
        />
        <span className="text-xs font-black tabular-nums" style={{ color: textColor }}>
          {streak}
        </span>
      </div>

      {/* Miles chip — mobil kompakt */}
      <div
        className="flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-50 border border-cyan-200 select-none shrink-0"
        role="img"
        aria-label={`${miles} mil`}
      >
        <Gem size={14} className="text-cyan-500" aria-hidden="true" strokeWidth={2.5} />
        <span className="text-xs font-black tabular-nums text-cyan-700">
          {milesFormatted}
        </span>
      </div>

      {/* Çan — mobil, Sheet variant */}
      <NotificationBell variant="mobile" />
    </div>
  );
}

/**
 * CabinLayout — Collapsible sidebar (desktop) + mobile drawer.
 * Sidebar state persisted to localStorage.
 * Gamification top bar (streak + gem + çan) tüm kabin sayfalarında görünür.
 */
export default function CabinLayout({ children }) {
  const location = useLocation();
  const { lang } = useLanguage();
  const { data: stats } = useCabinStats();

  const streak = stats?.streak ?? 0;
  const miles = stats?.totalMiles ?? 0;

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) === true;
    } catch {
      return false;
    }
  });

  const [mobileOpen, setMobileOpen] = useState(false);

  const handleToggle = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* localStorage erişilemez (private browsing) — sessizce görmezden gel */ }
      return next;
    });
  };

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-[var(--bg-app)]">
      <CabinSidebar
        isCollapsed={collapsed}
        onToggle={handleToggle}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main content */}
      <main
        className={`flex-1 transition-all duration-200 ease-out ${
          collapsed ? 'md:ml-16' : 'md:ml-64'
        }`}
        id="cabin-main-content"
      >
        {/* Mobile top bar — hamburger + logo + göstergeler + çan (tek satır) */}
        {/* overflow-x-hidden: 360px gibi dar ekranlarda yatay taşmayı önler */}
        <MobileTopBar
          streak={streak}
          miles={miles}
          lang={lang}
          onMenuOpen={() => setMobileOpen(true)}
        />

        {/* Desktop gamification bar — sidebar sağında, içerik üstünde, sticky */}
        <div className="hidden md:block">
          <CabinGamificationBar streak={streak} miles={miles} lang={lang} />
        </div>

        {children}
      </main>
    </div>
  );
}
